import { useHeaderSlot } from '@src/app/layout';
import { PAGE_LAYOUT_SCROLL_CONTAINER_ID } from '@src/app/layout/Page/constants';
import { ToastContainer } from '@src/app/notifications/toast';
import type { Theme } from '@src/app/themes';
import { AbsenceSocketController } from '@src/features/absence';
import {
  AlertSummarySocketController,
  NewAlertNotificationController,
} from '@src/features/alerts';
import { useAuthentication } from '@src/features/auth';
import { TaskSummarySocketController } from '@src/features/tasks';
import { UserPresenceSocketController } from '@src/features/users';
import { Box } from '@src/shared/ui/layout';
import { useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import TopMenu from './TopMenu';

const StyleSheet = {
  header: (isScrolled: boolean) => (theme: Theme) => ({
    display: 'flex',
    alignItems: 'ceneter',
    justifyContent: 'space-between',
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
    gap: 1.25,
    position: 'relative',
    backgroundColor: isScrolled ? 'grey.100' : 'transparent',
    boxShadow: isScrolled ? `0 -1px ${theme.palette.grey[200]} inset` : 'none',
    m: `-${theme.layout.content.topPadding}px -${theme.layout.content.rightPadding}px 0 -${theme.layout.content.leftPadding}px`,
    p: `${theme.layout.content.topPadding}px ${theme.layout.content.rightPadding}px 10px ${theme.layout.content.leftPadding}px`,
    transition: theme.transitions.create([
      'background-color',
      'box-shadow',
      'margin',
      'padding',
    ]),
  }),
  loginAsNotification: {
    position: 'fixed',
    top: '10px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'red.100',
    padding: '4px 16px',
    opacity: 0.8,
  },
};
export default function Header() {
  const { user, credentials } = useAuthentication();
  const { headerSlot } = useHeaderSlot();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [isScrolled, setIsScrolled] = useState(false);
  const loginAs = credentials?.loginAs ?? false;

  useEffect(() => {
    const syncFromScrollContainer = () => {
      const scrollContainer = document.getElementById(
        PAGE_LAYOUT_SCROLL_CONTAINER_ID,
      );
      setIsScrolled(Boolean(scrollContainer && scrollContainer.scrollTop > 0));
    };

    const onScroll = (event: Event) => {
      const target = event.target;
      if (
        !(target instanceof HTMLElement) ||
        target.id !== PAGE_LAYOUT_SCROLL_CONTAINER_ID
      ) {
        return;
      }

      setIsScrolled(target.scrollTop > 0);
    };

    syncFromScrollContainer();
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', syncFromScrollContainer);

    return () => {
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', syncFromScrollContainer);
    };
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Route changes intentionally reset the header scroll state.
  useEffect(() => {
    setIsScrolled(false);
  }, [pathname]);

  return (
    <Box sx={StyleSheet.header(isScrolled)}>
      {loginAs && (
        <Box sx={StyleSheet.loginAsNotification}>Login as {user?.name}</Box>
      )}
      <ToastContainer />
      <AlertSummarySocketController />
      <TaskSummarySocketController />
      <UserPresenceSocketController />
      <AbsenceSocketController />
      <NewAlertNotificationController />
      <TopMenu />
      {headerSlot}
    </Box>
  );
}
